import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { NavLink } from "react-router-dom"
import Button from "../../Button/Button"
import Input from "../../Input/Input"
import { showSnackBar } from "../../Snackbar/Snackbar"
import { validateEmail } from "../../../helpers/validationEmail"
import { isSignedMainDoc, getLsaTypeDoc } from "../../../features/documents/documents"
import { getLeadDetails } from "../../../features/contacts/contacts"
import { emailSigningLink, textSigningLink, SIGNING_LINK_NOT_SENT } from "../../../features/signingLink/signingLink"
import { toSavedCreditProfileIds } from "../../../containers/Contacts/add-contact/helper"
import useLsaChargeName from "../../../hooks/useLsaChargeName"
import LsaChargeNamePicker from "../../LsaChargeName/LsaChargeNamePicker"

const PANEL_ROLES = ['customer', 'user']
const MIN_PHONE_DIGITS = 10
const PHONE_HINT = 'Enter at least 10 digits'
const ALREADY_SIGNED = 'The client has already signed the agreement.'
const TITLE = 'Signing link'
const NOT_READY_TEXT = 'The lead details are not finished, so there is no agreement to sign yet. Finish them in the lead editor, then send the signing link.'
const READY_TEXT = 'The client has not signed the agreement yet. Send them the signing link: they add a payment method if needed, then sign.'
const AGREEMENT_OPTIONS = [
  { value: 'english', label: 'English' },
  { value: 'bilingual', label: 'Bilingual' },
]

const HIDDEN = { state: 'hidden' }
const NOT_READY = { state: 'notReady' }

const digitsOf = (value) => String(value ?? '').replace(/\D/g, '')

const hasEstimatedPayments = (lead) => (lead?.paymentSchedule?.estimatedPayments?.length || 0) > 0

const isFulfilled = (result) => result?.meta?.requestStatus === 'fulfilled'

const isSignedAnswer = (result) => result?.payload?.data === true

const lsaTypeOf = (result) => {
  if (!isFulfilled(result)) return null
  return result.payload?.data?.lsaType === 'bilingual' ? 'bilingual' : 'english'
}

const resolveAnswer = ([signedResult, leadResult, lsaResult]) => {
  const lead = leadResult?.payload?.data
  if (!isFulfilled(signedResult) || isSignedAnswer(signedResult) || !isFulfilled(leadResult) || !lead || typeof lead !== 'object') return HIDDEN
  const hasSavedDebts = toSavedCreditProfileIds(lead.creditSelect?.selectedCreditProfiles).length > 0
  if (!hasSavedDebts || !hasEstimatedPayments(lead)) return NOT_READY
  return { state: 'ready', lsaType: lsaTypeOf(lsaResult) }
}

const PanelCard = ({ text, children }) => (
  <div className="px-6 pb-6">
    <div className="bg-background border border-divider rounded-2xl p-6">
      <h3 className="text-lg font-bold text-text-primary mb-2">{TITLE}</h3>
      <p className="text-sm text-text-secondary mb-6">{text}</p>
      {children}
    </div>
  </div>
)

const AgreementToggle = ({ value, onChange }) => (
  <div className="flex items-center gap-2">
    <span className="text-sm font-medium text-text-primary">Agreement:</span>
    <div className="inline-flex rounded-lg border border-divider overflow-hidden">
      {AGREEMENT_OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          className={`px-4 py-1.5 text-sm font-medium transition-colors ${value === option.value ? 'bg-primary-main text-primary-contrast' : 'bg-transparent text-text-secondary hover:text-text-primary'}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  </div>
)

const SigningLinkPanel = ({ userId, contact, userRole, onOfferChange }) => {
  const dispatch = useDispatch()
  const eligible = PANEL_ROLES.includes(userRole) && contact?.status?.value === 'newLead'
  const [answer, setAnswer] = useState(null)
  const [emailEdit, setEmailEdit] = useState(null)
  const [phoneEdit, setPhoneEdit] = useState(null)
  const [agreementPick, setAgreementPick] = useState(null)
  const [sending, setSending] = useState(false)

  useEffect(() => {
    if (!eligible || !userId) return undefined
    let live = true
    setAnswer(null)
    Promise.all([
      dispatch(isSignedMainDoc({ userId })),
      dispatch(getLeadDetails(userId)),
      dispatch(getLsaTypeDoc({ userId })),
    ])
      .then((results) => { if (live) setAnswer(resolveAnswer(results)) })
      .catch(() => { if (live) setAnswer(HIDDEN) })
    return () => { live = false }
  }, [eligible, userId, dispatch])

  const shown = eligible && !!userId && (answer?.state === 'ready' || answer?.state === 'notReady')
  const offered = shown && answer?.state === 'ready'
  const chargeName = useLsaChargeName(offered ? userId : null)

  useEffect(() => {
    onOfferChange?.(offered)
  }, [offered, onOfferChange])

  if (!shown) return null

  if (answer.state === 'notReady') {
    return (
      <PanelCard text={NOT_READY_TEXT}>
        <NavLink to={`/lead/edit/${userId}`} className="text-md font-semibold text-primary-main">
          Open the lead editor
        </NavLink>
      </PanelCard>
    )
  }

  const email = emailEdit ?? (contact?.secondaryEmail || contact?.email || '')
  const phone = phoneEdit ?? digitsOf(contact?.primaryPhone)
  const lsaTemplateType = agreementPick ?? answer.lsaType
  const emailValid = validateEmail(email)
  const phoneValid = phone.length >= MIN_PHONE_DIGITS

  const send = async (request, successText) => {
    setSending(true)
    try {
      const check = await dispatch(isSignedMainDoc({ userId }))
      if (isFulfilled(check) && isSignedAnswer(check)) {
        showSnackBar('error', ALREADY_SIGNED)
        setAnswer(HIDDEN)
        return
      }
      const result = await dispatch(request)
      if (result?.error) {
        showSnackBar('error', result.payload || SIGNING_LINK_NOT_SENT)
      } else {
        showSnackBar('success', successText)
      }
    } catch (error) {
      showSnackBar('error', SIGNING_LINK_NOT_SENT)
    } finally {
      setSending(false)
    }
  }

  const sendEmail = () => send(
    emailSigningLink({ userId, email, lsaTemplateType }),
    `Signing link sent to ${email}`,
  )

  const sendText = () => send(
    textSigningLink({ userId, phoneNumber: phone, lsaTemplateType }),
    `Signing link texted to ${phone}`,
  )

  return (
    <PanelCard text={READY_TEXT}>
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-[240px]">
          <Input
            type="email"
            label="Email address"
            placeholder="Enter email"
            value={email}
            onChange={(e) => setEmailEdit(e.target.value)}
          />
        </div>
        <div className="flex-1 min-w-[240px]">
          <Input
            type="tel"
            label="Phone number"
            placeholder="Enter phone number"
            value={phone}
            onChange={(e) => setPhoneEdit(digitsOf(e.target.value))}
            errorMsg={phone.length > 0 && !phoneValid ? PHONE_HINT : ''}
          />
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-4 mt-6">
        <AgreementToggle value={lsaTemplateType} onChange={setAgreementPick} />
        {chargeName.required && <LsaChargeNamePicker userId={userId} />}
        <div className="flex gap-3">
          <Button disabled={sending || !emailValid || !!chargeName.blocked} onClick={sendEmail}>
            Email link
          </Button>
          <Button type="outlined" disabled={sending || !phoneValid || !!chargeName.blocked} onClick={sendText}>
            Text link
          </Button>
        </div>
      </div>
    </PanelCard>
  )
}

export default SigningLinkPanel
